/**
 * Seeded directory of free (and some paid) state and county public records
 * useful for locating easements, rights-of-way, and infrastructure corridors.
 *
 * Typed like a future database response so the UI can be repointed at a backend
 * query later without changing component props.
 */

export type AccessType = "free-gis" | "web-searchable" | "rest-arcgis" | "paywall";

export interface PublicRecordSource {
  id: string;
  /** USPS state code, e.g. "TX". */
  state: string;
  stateName: string;
  /** County name, if the source is county-specific. */
  county?: string;
  /** Five-digit county FIPS code, when known. */
  fips?: string;
  /** Human-readable source title. */
  title: string;
  /** Agency or department that publishes the data. */
  agency: string;
  /** Asset classes this source covers. */
  assetClasses: string[];
  /** How the data can be accessed. */
  accessType: AccessType;
  /** Direct URL to the portal, service directory, or REST endpoint. */
  url: string;
  /** Free-form notes on coverage, quirks, or disclaimers. */
  notes: string;
  /** Searchable tags beyond asset classes. */
  tags: string[];
}

export const ACCESS_TYPE_LABEL: Record<AccessType, string> = {
  "free-gis": "Free GIS Endpoint",
  "web-searchable": "Web Searchable",
  "rest-arcgis": "REST/ArcGIS API",
  paywall: "Paywall/Subscription",
};

export const ACCESS_TYPE_CHIP: Record<AccessType, string> = {
  "free-gis": "Free GIS Endpoints",
  "web-searchable": "Web Searchable",
  "rest-arcgis": "REST/ArcGIS APIs",
  paywall: "Paywall/Subscription",
};

export const PUBLIC_RECORDS: PublicRecordSource[] = [
  {
    id: "us-fema-nfhl",
    state: "US",
    stateName: "National",
    title: "FEMA National Flood Hazard Layer (NFHL)",
    agency: "Federal Emergency Management Agency",
    assetClasses: ["Flood Hazard", "Regulatory Floodplain"],
    accessType: "rest-arcgis",
    url: "https://hazards-fema.maps.arcgis.com/home/item.html?id=8c6e2171a7ff4d8e8a111f7c91df6d5b",
    notes:
      "Authoritative flood zone polygons and FIRM panels for the United States. Best for SFHA checks; rural panels may be older or generalized.",
    tags: ["fema", "flood", "nfhl", "sfha", "firm"],
  },
  {
    id: "us-usgs-3dep",
    state: "US",
    stateName: "National",
    title: "USGS 3D Elevation Program (3DEP / EPQS)",
    agency: "U.S. Geological Survey",
    assetClasses: ["Ground Elevation", "Topography"],
    accessType: "rest-arcgis",
    url: "https://apps.nationalmap.gov/epqs/",
    notes:
      "Point elevation queries from the National Elevation Dataset. Resolution varies from 1/3 arc-second to 1-meter in lidar-covered areas.",
    tags: ["usgs", "elevation", "3dep", "epqs", "topography"],
  },
  {
    id: "us-usda-ssurgo",
    state: "US",
    stateName: "National",
    title: "USDA NRCS Soil Data Access (SSURGO)",
    agency: "Natural Resources Conservation Service",
    assetClasses: ["Soil Survey", "Hydrologic Soil Group"],
    accessType: "rest-arcgis",
    url: "https://sdmdataaccess.nrcs.usda.gov/",
    notes:
      "SSURGO map-unit data for soil classification, drainage class, and hydrologic group. Updated on county schedules; not a site-specific soil investigation.",
    tags: ["usda", "soil", "ssurgo", "nrcs", "drainage"],
  },
  {
    id: "us-hud-qoz",
    state: "US",
    stateName: "National",
    title: "HUD Qualified Opportunity Zones",
    agency: "U.S. Department of Treasury / CDFI Fund",
    assetClasses: ["Opportunity Zone", "Economic Development"],
    accessType: "rest-arcgis",
    url: "https://www.cdfifund.gov/opportunity-zones",
    notes:
      "Census tract boundaries designated as Qualified Opportunity Zones. Tracts are fixed by legislation and do not change annually.",
    tags: ["hud", "qoz", "opportunity zone", "treasury"],
  },
  {
    id: "us-osm-overpass",
    state: "US",
    stateName: "National",
    title: "OpenStreetMap Overpass Infrastructure",
    agency: "OpenStreetMap Contributors",
    assetClasses: ["Power Line", "Pipeline", "Rail", "Road ROW"],
    accessType: "rest-arcgis",
    url: "https://overpass-api.de/",
    notes:
      "Community-mapped utility centerlines and rights-of-way. Coverage is excellent near populated areas but sparse or unverified in remote regions.",
    tags: ["osm", "overpass", "power", "pipeline", "rail", "row"],
  },
  {
    id: "tx-txdot-row",
    state: "TX",
    stateName: "Texas",
    title: "TxDOT Open Data Portal: Right of Way",
    agency: "Texas Department of Transportation",
    assetClasses: ["DOT Right of Way", "Highway Corridor", "Rail"],
    accessType: "rest-arcgis",
    url: "https://gis-txdot.opendata.arcgis.com/",
    notes:
      "State-maintained roadways, ROW parcels, and rail corridors. Counties maintain their own local roads, so this is strongest for state highways.",
    tags: ["txdot", "right of way", "highway", "rail"],
  },
  {
    id: "tx-texas-parcels",
    state: "TX",
    stateName: "Texas",
    title: "Texas StratMap Parcels",
    agency: "Texas Natural Resources Information System",
    assetClasses: ["Parcel Boundary", "Tax Parcel"],
    accessType: "rest-arcgis",
    url: "https://data.tnris.org/collection/",
    notes:
      "Statewide parcel aggregation from county tax rolls. Vintage varies by county; always verify against the county clerk's recorded plat.",
    tags: ["tnris", "parcel", "stratmap", "tax"],
  },
  {
    id: "ca-caltrans-row",
    state: "CA",
    stateName: "California",
    title: "Caltrans GIS Data: Right of Way",
    agency: "California Department of Transportation",
    assetClasses: ["DOT Right of Way", "Highway Corridor"],
    accessType: "rest-arcgis",
    url: "https://gisdata-caltrans.opendata.arcgis.com/",
    notes:
      "State highway ROW and linear referencing data. Local streets and county roads are maintained by cities/counties separately.",
    tags: ["caltrans", "right of way", "highway", "linear referencing"],
  },
  {
    id: "ca-ca-parcels",
    state: "CA",
    stateName: "California",
    title: "California Statewide Parcels",
    agency: "California Department of Technology / GeoPortal",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://gis.data.ca.gov/",
    notes:
      "Open parcel layer compiled from county assessors. Best for urban counties; rural counties may have gaps or older vintages.",
    tags: ["ca geoportal", "parcel", "assessor"],
  },
  {
    id: "fl-fdot-row",
    state: "FL",
    stateName: "Florida",
    title: "FDOT GIS Data: Right of Way",
    agency: "Florida Department of Transportation",
    assetClasses: ["DOT Right of Way", "Bridge", "Rail"],
    accessType: "rest-arcgis",
    url: "https://www.fdot.gov/agileassets/",
    notes:
      "State road inventory and ROW features. Search by county or state road number; local roads are not included.",
    tags: ["fdot", "right of way", "state road", "bridge"],
  },
  {
    id: "fl-florida-parcels",
    state: "FL",
    stateName: "Florida",
    title: "Florida Statewide Cadastral Parcels",
    agency: "Florida Department of Revenue / FGDL",
    assetClasses: ["Parcel Boundary", "Tax Parcel"],
    accessType: "rest-arcgis",
    url: "https://www.fgdl.org/",
    notes:
      "Statewide parcel mosaic from county property appraisers. FGDL membership may be required for some downloads; the REST service is generally public.",
    tags: ["fgdl", "parcel", "cadastral", "appraiser"],
  },
  {
    id: "nc-ncdot-row",
    state: "NC",
    stateName: "North Carolina",
    title: "NCDOT GIS Data: Right of Way",
    agency: "North Carolina Department of Transportation",
    assetClasses: ["DOT Right of Way", "Rail Corridor"],
    accessType: "rest-arcgis",
    url: "https://www.arcgis.com/home/item.html?id=7e6a6adbdf3e4c22b9e2a9b7f6f9f0b7",
    notes:
      "State-maintained road and rail ROW. Municipal streets are maintained locally; use county parcel layers for local road frontage.",
    tags: ["ncdot", "right of way", "rail", "state road"],
  },
  {
    id: "nc-nc-parcels",
    state: "NC",
    stateName: "North Carolina",
    title: "NC OneMap Parcels",
    agency: "NC State University / NC OneMap",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://www.nconemap.gov/datasets/nc1map-parcels",
    notes:
      "Statewide parcel service with county-level updates. Good geometry coverage; attribute richness varies by county.",
    tags: ["nconemap", "parcel", "onemap"],
  },
  {
    id: "va-virginia-parcels",
    state: "VA",
    stateName: "Virginia",
    title: "Virginia Parcels",
    agency: "Virginia Geographic Information Network",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://vginmaps.vdem.virginia.gov/arcgis/rest/services/VA_Base_Layers/VA_Parcels",
    notes:
      "Statewide parcel rest service maintained by VGIN. Useful for ROW width checks against tax-map frontage.",
    tags: ["vgin", "parcel", "tax map"],
  },
  {
    id: "ny-ny-parcels",
    state: "NY",
    stateName: "New York",
    title: "NYS Tax Parcels",
    agency: "New York State Department of Taxation and Finance",
    assetClasses: ["Parcel Boundary", "Tax Parcel"],
    accessType: "rest-arcgis",
    url: "https://gis.ny.gov/parcels",
    notes:
      "Statewide tax parcel file refreshed annually. New York City parcels are maintained separately by NYC OpenData.",
    tags: ["nys", "parcel", "tax", "assessor"],
  },
  {
    id: "il-cook-parcels",
    state: "IL",
    stateName: "Illinois",
    county: "Cook",
    fips: "17031",
    title: "Cook County Parcel Map",
    agency: "Cook County Assessor",
    assetClasses: ["Parcel Boundary"],
    accessType: "web-searchable",
    url: "https://www.cookcountyassessor.com/",
    notes:
      "Web map for Cook County parcels and assessment records. Illinois has no statewide parcel service; each county maintains its own.",
    tags: ["cook county", "parcel", "assessor", "illinois"],
  },
  {
    id: "il-dupage-parcels",
    state: "IL",
    stateName: "Illinois",
    county: "DuPage",
    fips: "17043",
    title: "DuPage County Open Data Parcels",
    agency: "DuPage County GIS",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://services.arcgis.com/neJvtQ4PXvnQ86MJ/arcgis/rest/services/AssessmentParcels2022_RealEstateFile2022/FeatureServer/3",
    notes:
      "ArcGIS REST parcel polygons for DuPage County. Attribute fields include PIN, address, and assessed values.",
    tags: ["dupage", "parcel", "arcgis", "illinois"],
  },
  {
    id: "co-colorado-parcels",
    state: "CO",
    stateName: "Colorado",
    title: "Colorado Public Parcels",
    agency: "Colorado State Land Board / DOLA",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://gis.colorado.gov/public/rest/services/Address_and_Parcel/Colorado_Public_Parcels/FeatureServer/0",
    notes:
      "Statewide parcel service with quarterly updates. Strong coverage for frontage and boundary checks.",
    tags: ["colorado", "parcel", "dola", "state land board"],
  },
  {
    id: "mn-minnesota-parcels",
    state: "MN",
    stateName: "Minnesota",
    title: "Minnesota Open Parcels",
    agency: "Minnesota Geospatial Commons / MNGEO",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://gisdata.mn.gov/dataset/us-mn-state-mngeo-plan-parcels-open",
    notes:
      "Open parcel mosaic compiled from county assessors. Good attribute standardization across counties.",
    tags: ["minnesota", "parcel", "mngeo", "geospatial commons"],
  },
  {
    id: "wa-washington-parcels",
    state: "WA",
    stateName: "Washington",
    title: "Washington State Current Parcels",
    agency: "Washington State Department of Natural Resources",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://services.arcgis.com/jsIt88o09Q0r1j8h/arcgis/rest/services/Current_Parcels/FeatureServer/0",
    notes:
      "Statewide parcel layer maintained by DNR. Useful for utility and telecom ROW planning across western Washington.",
    tags: ["washington", "parcel", "dnr"],
  },
  {
    id: "in-indiana-parcels",
    state: "IN",
    stateName: "Indiana",
    title: "Indiana Parcel Boundaries",
    agency: "Indiana Geographic Information Council",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://gisdata.in.gov/server/rest/services/Hosted/Parcel_Boundaries_of_Indiana_2022/FeatureServer/0",
    notes:
      "Statewide parcel boundaries from IGIC. Updated on a multi-year cycle; verify recent subdivisions locally.",
    tags: ["indiana", "parcel", "igic"],
  },
  {
    id: "oh-ohio-parcels",
    state: "OH",
    stateName: "Ohio",
    title: "Ohio Statewide Parcels",
    agency: "Ohio Geographically Referenced Information Program",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://ogrip.oit.ohio.gov/",
    notes:
      "OGRIP statewide parcel service. County participation varies; some counties publish richer attributes than others.",
    tags: ["ohio", "parcel", "ogrip"],
  },
  {
    id: "az-arizona-parcels",
    state: "AZ",
    stateName: "Arizona",
    title: "Arizona Statewide Parcels",
    agency: "Arizona State Land Department / AZGeo",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://azgeo.az.gov/arcgis/rest/services/TerraSystems/AZParcel_Cache/FeatureServer/0",
    notes:
      "AZGeo parcel cache covering most Arizona counties. Helpful for solar and telecom site screening.",
    tags: ["arizona", "parcel", "azgeo", "solar", "telecom"],
  },
  {
    id: "ut-utah-parcels",
    state: "UT",
    stateName: "Utah",
    title: "Utah Statewide Parcels",
    agency: "Utah Automated Geographic Reference Center",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://opendata.gis.utah.gov/datasets/utah-statewide-parcels",
    notes:
      "Regularly updated parcel mosaic from county recorders. Excellent for drone and green-energy siting work.",
    tags: ["utah", "parcel", "agrc", "opendata"],
  },
  {
    id: "de-delaware-parcels",
    state: "DE",
    stateName: "Delaware",
    title: "Delaware State Parcels",
    agency: "Delaware FirstMap",
    assetClasses: ["Parcel Boundary"],
    accessType: "rest-arcgis",
    url: "https://enterprise.firstmap.delaware.gov/arcgis/rest/services/PlanningCadastre/DE_StateParcels/FeatureServer/0",
    notes:
      "Statewide parcel service from FirstMap. Small state with consistent coverage across all three counties.",
    tags: ["delaware", "parcel", "firstmap"],
  },
  {
    id: "sample-county-recorder",
    state: "US",
    stateName: "County-by-county",
    title: "County Recorder / Clerk of Courts Deed Search",
    agency: "Local County Recorder",
    assetClasses: ["Recorded Easement", "Deed", "Plat", "ROW Document"],
    accessType: "paywall",
    url: "https://www.county-recorder-search.example/",
    notes:
      "Most counties offer paid document search portals for recorded easements, plats, and covenants. Pricing and coverage vary; some offer free guest access with limited pages.",
    tags: ["recorder", "deed", "plat", "recorded easement"],
  },
];

export function deriveStates(records: PublicRecordSource[]): string[] {
  const set = new Set(records.map((r) => r.stateName));
  return Array.from(set).sort();
}

export function deriveAssetClasses(records: PublicRecordSource[]): string[] {
  const set = new Set<string>();
  for (const record of records) {
    for (const cls of record.assetClasses) set.add(cls);
  }
  return Array.from(set).sort();
}

export function deriveAccessTypes(): AccessType[] {
  return ["free-gis", "web-searchable", "rest-arcgis", "paywall"];
}
